import { useCallback, useEffect, useId, useRef, useState, type MouseEvent } from 'react'
import { useIntl } from 'react-intl'
import { Link, useLocation } from 'react-router'

import { adminHomeHref, adminHomePath, useAdminHomeGroups, type AdminHomeGroup } from '@/shared/registry'
import { cn, focusRing, insetFocusRing } from '@/shared/ui/cn'
import { isPlainClick } from '@/shared/ui/events'
import { ChevronDown, ChevronUp } from '@/shared/ui/icons'

const itemClass = cn(
  'block w-full px-3 py-2 text-left text-sm text-text-primary transition hover:bg-background-contrast-sm',
  insetFocusRing,
)

/**
 * The Admin menu, first in the nav as the mockup's "Admin" item: Admin Home, then each of its
 * sections that holds a link the viewer may see; nothing for a viewer with no such link. A
 * disclosure, as the account menu is: Tab moves through it, and Escape, a click outside or focus
 * moving outside closes it.
 */
export function AdminMenu() {
  const groups = useAdminHomeGroups()
  // Only someone with an Admin Home link to see has an Admin menu; losing them unmounts it, listeners and all.
  return groups.length === 0 ? null : <AdminMenuDisclosure groups={groups} />
}

function AdminMenuDisclosure(props: { readonly groups: readonly AdminHomeGroup[] }) {
  const { groups } = props
  const intl = useIntl()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelId = useId()
  const active = pathname === adminHomePath || pathname.startsWith(`${adminHomePath}/`)

  // A link opened in another tab or window leaves this page, and its focus, where they are.
  const close = useCallback((event: MouseEvent) => {
    if (isPlainClick(event)) {
      setOpen(false)
    }
  }, [])
  const toggle = useCallback(() => setOpen((value) => !value), [])

  useEffect(() => {
    const container = containerRef.current
    if (!open || container === null) {
      return undefined
    }
    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node) || !container.contains(event.target)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    const onFocusOut = (event: FocusEvent) => {
      if (event.relatedTarget instanceof Node && !container.contains(event.relatedTarget)) {
        setOpen(false)
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    container.addEventListener('focusout', onFocusOut)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      container.removeEventListener('focusout', onFocusOut)
    }
  }, [open])

  return (
    // Narrower than md the panel hangs from the whole nav, below its last row, at its full width,
    // so it never half-covers a nav item that wrapped (WCAG 2.5.8); wider, from the button.
    <div ref={containerRef} className="md:relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        className={cn(
          'flex min-h-6 cursor-pointer items-center gap-1 rounded text-sm transition',
          focusRing,
          active
            ? 'font-semibold text-primary-main'
            : 'font-medium text-text-primary hover:opacity-70',
        )}
      >
        {intl.formatMessage({ id: 'shell.nav.admin' })}
        {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </button>

      {open && (
        // Where the header is sticky, the panel scrolls within the room below it, so its last
        // section stays reachable on a short screen or at a high zoom.
        <div
          id={panelId}
          className="absolute inset-x-0 top-full z-50 mt-2 overflow-y-auto md:right-auto md:w-64 overscroll-contain md:max-h-[calc(100dvh-var(--shell-header-height,0px)-1rem)] rounded border border-text-primary bg-background text-left shadow-card-hover"
        >
          <Link to={adminHomeHref()} onClick={close} className={cn(itemClass, 'font-medium')}>
            {intl.formatMessage({ id: 'shell.nav.adminHome' })}
          </Link>
          <ul className="border-t border-divider">
            {groups.map(({ section }) => (
              <li key={section.id}>
                <Link to={adminHomeHref(section.id)} onClick={close} className={itemClass}>
                  {intl.formatMessage({ id: section.titleId })}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
